import { useState } from 'react';
import { violationService } from '../../clients/ApiClients';
import { AttachmentReturn } from '../../clients/rust-api-client';
import { billingErrorMessage } from '../../utils/billingHelpers';
import { ErrorAlert } from '../../common/Custom/ErrorAlert';
import AttachmentThumb from './AttachmentThumb';
import AttachmentPicker from './AttachmentPicker';
import { useAttachmentUploads } from './useAttachmentUploads';

interface Props {
  violationId: string;
  attachments: AttachmentReturn[];
  canManage: boolean;
  /** Re-read the violation so the new attachments come back with it. */
  onChanged: () => void;
}

/**
 * The violation's own photos and documents — management's evidence for
 * the citation, as distinct from a photo in somebody's reply.
 *
 * There is no phase-one problem here: the violation already exists, so an
 * upload attaches straight away and a failure is retried on its own.
 */
export function ViolationAttachments({
  violationId,
  attachments,
  canManage,
  onChanged,
}: Props) {
  const uploads = useAttachmentUploads();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleUpload = async () => {
    setBusy(true);
    setError(null);
    const clean = await uploads.uploadAll({
      kind: 'violation',
      id: violationId,
    });
    setBusy(false);
    onChanged();
    if (clean) uploads.reset();
  };

  const handleRemove = async (attachment: AttachmentReturn) => {
    if (!window.confirm(`Remove ${attachment.file_name}?`)) return;
    setBusy(true);
    setError(null);
    try {
      // `attachment.id` detaches. `attachment.file_id` is the other one,
      // and is what the inline route takes — swapping them is a 404 that
      // reads like a missing file.
      await violationService.deleteViolationAttachment({
        id: violationId,
        attachmentId: attachment.id,
      });
      onChanged();
    } catch (err) {
      console.error('Error removing attachment:', err);
      setError(billingErrorMessage(err, 'That file could not be removed.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {attachments.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No photos or documents on this violation.
        </p>
      ) : (
        <div className="flex flex-wrap items-start gap-2">
          {attachments.map((attachment) => (
            <AttachmentThumb
              key={attachment.id}
              attachment={attachment}
              onRemove={canManage ? handleRemove : undefined}
            />
          ))}
        </div>
      )}

      {canManage && (
        <div className="mt-3">
          <AttachmentPicker
            staged={uploads.staged}
            disabled={busy}
            label="Add photos or documents"
            onStage={uploads.stage}
            onRemove={uploads.remove}
          />
          {uploads.hasUploadable && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={busy}
              className="mt-2 text-sm text-sky-600 hover:underline disabled:text-gray-500 dark:text-sky-400"
            >
              {busy
                ? 'Uploading…'
                : uploads.hasFailures
                  ? 'Retry upload'
                  : 'Upload'}
            </button>
          )}
        </div>
      )}

      <ErrorAlert className="mt-2">{error}</ErrorAlert>
    </div>
  );
}

export default ViolationAttachments;
